/**
 * Owner · distributor partners (GET /admin/distributors).
 * One row per partner org — share, roster size, MTD GMV, our cut, payout owed vs paid — and a
 * drill-in that carries the two owner actions that had no home before: adjusting the share, and
 * actually PAYING a partner (POST /admin/distributors/{orgId}/payouts, server-computed + idempotent).
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FX_TO_INR, type BillingCurrency } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { currencyMinor, inrCompact } from "../../lib/format.js";
import { Alert, Info } from "../../components/icons.js";
import { Modal, apiErrorText } from "./adminUi.js";

interface PartnerClient {
  orgId: string;
  name: string;
  currency: BillingCurrency;
  walletUnits: number;
  revenueMtdMinor: number;
  /** 0094: lifetime paid purchases + revenue collected excluding GST */
  purchases: number;
  revenueLifetimeMinor: number;
  createdAt: string;
}

/** A settlement month: what it earned, what we owe for it, and the payout row if it's settled. */
interface PartnerPeriod {
  periodStart: string;
  revenueMinor: number;
  /** Exactly what POST …/payouts will record for this period if run now. */
  accrualMinor: number;
  payout: {
    status: string;
    currency: string;
    revenueMinor: number;
    sharePct: number;
    payoutMinor: number;
    paidAt: string | null;
    method: string | null;
    note: string | null;
  } | null;
}

interface Partner {
  orgId: string;
  name: string;
  currency: BillingCurrency;
  symbol: string;
  sharePct: number;
  joinedAt: string;
  clientOrgs: number;
  gmvMtdMinor: number;
  cutMtdMinor: number;
  paidMinor: number;
  owedMinor: number;
  purchases: number;
  revenueLifetimeMinor: number;
  /** earned = paid + owed (lifetime commission) */
  earnedMinor: number;
  /** >1 entry means the commission sum mixes minor units across currencies — say so, don't hide it. */
  clientCurrencies: BillingCurrency[];
  clients: PartnerClient[];
  periods: PartnerPeriod[];
}

interface PartnersResponse {
  currentPeriod: string | null;
  distributors: Partner[];
}

/** Cross-partner totals only make sense on one axis — the same ₹-eq peg the Revenue page uses. */
const inrEq = (minor: number, currency: BillingCurrency): number =>
  Math.round((minor / 100) * FX_TO_INR[currency]);

const monthLabel = (periodStart: string): string =>
  new Date(`${periodStart}T00:00:00Z`).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

export function Partners(): ReactNode {
  const { me } = useAuth();
  const isOwner = me?.role === "owner";
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-partners"],
    queryFn: () => api.get<PartnersResponse>("/admin/distributors"),
    enabled: isOwner,
  });
  const partners = data?.distributors ?? [];

  const [openId, setOpenId] = useState<string | null>(null);
  const selected = partners.find((p) => p.orgId === openId) ?? null;

  // --- share editor (moved off the org drill-in: a partner's share is partner business) ---
  const [shareDraft, setShareDraft] = useState("");
  const refresh = (): void => {
    // /orgs carries isDistributor + distSharePct too, so the client-orgs table stays honest.
    void queryClient.invalidateQueries({ queryKey: ["admin-partners"] });
    void queryClient.invalidateQueries({ queryKey: ["orgs"] });
  };
  const setShare = useMutation({
    mutationFn: (v: { orgId: string; sharePct: number }) =>
      api.put(`/admin/distributors/${v.orgId}/share`, { sharePct: v.sharePct }),
    onSuccess: () => {
      setShareDraft("");
      refresh();
    },
  });

  // --- record a payout: pick a period, confirm the server-computed amount, log it ---
  const [payPeriod, setPayPeriod] = useState<PartnerPeriod | null>(null);
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");
  const closePay = (): void => {
    setPayPeriod(null);
    setMethod("");
    setNote("");
  };
  const recordPayout = useMutation({
    mutationFn: (v: { orgId: string; periodStart: string }) =>
      api.post(`/admin/distributors/${v.orgId}/payouts`, {
        periodStart: v.periodStart,
        method: method.trim() || undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      closePay();
      refresh();
    },
  });

  if (!isOwner) {
    return (
      <section>
        <div className="page-h">
          <div>
            <h1>Partners</h1>
            <div className="sub">Distributor economics and payouts</div>
          </div>
          <span className="pill n">Owner</span>
        </div>
        <div className="card pad mut sm">
          Partner commercials are Owner-only — shares and payouts move real money out of the platform.
        </div>
      </section>
    );
  }

  const totalGmvInrEq = partners.reduce((s, p) => s + inrEq(p.gmvMtdMinor, p.currency), 0);
  const totalOwedInrEq = partners.reduce((s, p) => s + inrEq(p.owedMinor, p.currency), 0);
  const totalClients = partners.reduce((s, p) => s + p.clientOrgs, 0);

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Partners</h1>
          <div className="sub">
            Distributors who resell TalentHop · share, roster, GMV and payouts · live from the API
          </div>
        </div>
        <span className="pill n">Owner</span>
      </div>

      {isLoading && <div className="card pad mut sm">Loading partners…</div>}

      {isError && (
        <div className="banner crit">
          <span>{apiErrorText(error, "Couldn't load partners. Please retry in a moment.")}</span>
        </div>
      )}

      {!isLoading && !isError && partners.length === 0 && (
        <div className="card pad mut sm">
          No distributor partners yet. Flag an org as a distributor when you create it on{" "}
          <Link to="/admin/orgs">Client orgs</Link>; the orgs it signs stay on that page too, so revenue
          still reconciles in one place.
        </div>
      )}

      {partners.length > 0 && (
        <>
          <div className="grid g4" style={{ marginBottom: 16 }}>
            <div className="card pad stat">
              <div className="l">Partners</div>
              <div className="n tnum">{partners.length}</div>
              <div className="d">active distributors</div>
            </div>
            <div className="card pad stat">
              <div className="l">Client orgs sourced</div>
              <div className="n tnum">{totalClients}</div>
              <div className="d">tenants signed through partners</div>
            </div>
            <div className="card pad stat">
              <div className="l">Partner GMV · MTD (₹-eq)</div>
              <div className="n tnum">{inrCompact(totalGmvInrEq)}</div>
              <div className="d">their clients' purchases, excl. GST</div>
            </div>
            <div className="card pad stat">
              <div className="l">Owed to partners (₹-eq)</div>
              <div className="n violet tnum">{inrCompact(totalOwedInrEq)}</div>
              <div className="d">accrued, not yet paid out</div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Distributor partners</h3>
              <span className="mut sm">Click a row for roster, payouts and share</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Partner</th>
                    <th>Currency</th>
                    <th>Share</th>
                    <th>Client orgs</th>
                    <th>Purchases</th>
                    <th>Revenue · lifetime</th>
                    <th>GMV · MTD</th>
                    <th>Earned</th>
                    <th>Owed / paid</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((p) => (
                    <tr
                      key={p.orgId}
                      className="clickable"
                      onClick={() => {
                        // A half-typed rate must not follow the operator to a different partner.
                        setShareDraft("");
                        setOpenId(p.orgId === openId ? null : p.orgId);
                      }}
                    >
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>{p.currency}</td>
                      <td className="tnum">{p.sharePct}%</td>
                      <td className="tnum">{p.clientOrgs}</td>
                      <td className="tnum">{p.purchases}</td>
                      <td className="tnum">{currencyMinor(p.revenueLifetimeMinor, p.currency)}</td>
                      <td className="tnum">{currencyMinor(p.gmvMtdMinor, p.currency)}</td>
                      <td className="tnum">{currencyMinor(p.earnedMinor, p.currency)}</td>
                      <td className="tnum">
                        <span className={p.owedMinor > 0 ? "pill r" : "pill v"}>
                          {currencyMinor(p.owedMinor, p.currency)} owed
                        </span>
                        <span className="mut sm" style={{ marginLeft: 6 }}>
                          {currencyMinor(p.paidMinor, p.currency)} paid
                        </span>
                      </td>
                      <td className="mut sm tnum">{new Date(p.joinedAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {selected && (
        <>
          {selected.clientCurrencies.length > 1 && (
            <div className="banner crit" style={{ marginBottom: 16 }}>
              <Alert />
              <span>
                <b>{selected.name}</b> has client orgs billing in{" "}
                {selected.clientCurrencies.join(", ")}. Commission sums purchase amounts as-is, so
                these figures mix minor units across currencies — settle those clients manually until
                per-currency payouts land.
              </span>
            </div>
          )}

          <div className="grid g2" style={{ marginBottom: 16 }}>
            <div className="card">
              <div className="card-h">
                <h3>{selected.name} · share</h3>
                <span className="tag">Owner</span>
              </div>
              <div className="card-b">
                <p className="mut sm" style={{ marginTop: 0 }}>
                  This partner earns a share of every purchase its client orgs make — lifetime, gross,
                  settled monthly. The partner console reflects a change immediately; periods already
                  paid keep the rate they were paid at.
                </p>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span className="pill t tnum">current · {selected.sharePct}%</span>
                  <input
                    className="input tnum"
                    style={{ width: 90 }}
                    type="number"
                    min={0.5}
                    max={50}
                    step={0.5}
                    placeholder="new %"
                    value={shareDraft}
                    onChange={(e) => setShareDraft(e.target.value)}
                  />
                  <button
                    className="btn sm primary"
                    disabled={
                      setShare.isPending || !(Number(shareDraft) >= 0.5 && Number(shareDraft) <= 50)
                    }
                    onClick={() =>
                      setShare.mutate({ orgId: selected.orgId, sharePct: Number(shareDraft) })
                    }
                  >
                    {setShare.isPending ? "Saving…" : "Update share"}
                  </button>
                </div>
                {setShare.isError && (
                  <div className="banner crit" style={{ marginTop: 12 }}>
                    <span>{apiErrorText(setShare.error, "Couldn't update the share. Please retry.")}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h3>Client roster</h3>
                <span className="mut sm">{selected.clients.length} orgs</span>
              </div>
              <div className="card-b">
                {selected.clients.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    This partner hasn't signed any orgs yet.
                  </p>
                )}
                {selected.clients.map((c) => (
                  <div key={c.orgId} className="krow">
                    <span className="k">
                      <Link to={`/admin/orgs/${c.orgId}`}>{c.name}</Link>
                      <span className="mut sm">
                        {" "}· {c.purchases} purchase{c.purchases === 1 ? "" : "s"} ·{" "}
                        {currencyMinor(c.revenueLifetimeMinor, c.currency)} lifetime
                      </span>
                    </span>
                    <span className="v tnum">{currencyMinor(c.revenueMtdMinor, c.currency)} · MTD</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Payouts · {selected.name}</h3>
              <span className="mut sm">Amounts are computed server-side from paid purchases</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Client purchases</th>
                    <th>Payout ({selected.sharePct}%)</th>
                    <th>Status</th>
                    <th>Method</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {selected.periods.map((p) => {
                    const settled = p.payout?.status === "paid";
                    return (
                      <tr key={p.periodStart}>
                        <td style={{ fontWeight: 600 }}>
                          {monthLabel(p.periodStart)}
                          {/* Settling the running month pays only what has settled so far. */}
                          {p.periodStart === data?.currentPeriod && (
                            <span className="mut sm" style={{ fontWeight: 400 }}>
                              {" "}
                              · in progress
                            </span>
                          )}
                        </td>
                        <td className="tnum">
                          {currencyMinor(
                            settled ? p.payout!.revenueMinor : p.revenueMinor,
                            selected.currency,
                          )}
                        </td>
                        <td className="tnum">
                          {currencyMinor(
                            settled ? p.payout!.payoutMinor : p.accrualMinor,
                            selected.currency,
                          )}
                          {/* A settled period keeps the rate it was paid at, even after a re-rate. */}
                          {settled && p.payout!.sharePct !== selected.sharePct && (
                            <span className="mut sm" style={{ marginLeft: 6 }}>
                              at {p.payout!.sharePct}%
                            </span>
                          )}
                        </td>
                        <td>
                          {settled ? (
                            <span className="pill v">
                              paid {p.payout!.paidAt ? new Date(p.payout!.paidAt).toLocaleDateString() : ""}
                            </span>
                          ) : (
                            <span className="pill r">owed</span>
                          )}
                        </td>
                        <td className="mut sm">{p.payout?.method ?? "—"}</td>
                        <td>
                          <button
                            className="btn sm ghost"
                            onClick={() => {
                              setPayPeriod(p);
                              setMethod(p.payout?.method ?? "");
                              setNote(p.payout?.note ?? "");
                            }}
                          >
                            {settled ? "Re-record" : "Record payout"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {payPeriod && selected && (
        <Modal title={`Pay ${selected.name} · ${monthLabel(payPeriod.periodStart)}`} onClose={closePay}>
          <p className="mut sm" style={{ marginTop: 0 }}>
            The amount is recomputed from settled purchases when you confirm, so it can't drift from
            the ledger. Recording the same period twice updates that one row instead of paying twice.
          </p>
          <div className="krow">
            <span className="k">Client purchases</span>
            <span className="v tnum">{currencyMinor(payPeriod.revenueMinor, selected.currency)}</span>
          </div>
          <div className="krow">
            <span className="k">Payout at {selected.sharePct}%</span>
            <span className="v tnum" style={{ fontWeight: 700 }}>
              {currencyMinor(payPeriod.accrualMinor, selected.currency)}
            </span>
          </div>
          {payPeriod.periodStart === data?.currentPeriod && (
            <div className="banner info" style={{ margin: "12px 0" }}>
              <Info />
              <span>
                This month is still running — you'd be settling purchases up to now. Later purchases
                in the same month won't re-open it; re-record the period to top the payout up.
              </span>
            </div>
          )}
          <div className="field" style={{ margin: "12px 0 10px" }}>
            <label>Method</label>
            <input
              className="input"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              placeholder="e.g. NEFT · HDFC ****4417"
            />
          </div>
          <div className="field" style={{ marginBottom: 12 }}>
            <label>Note (optional)</label>
            <input
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. UTR 2026072900431"
            />
          </div>
          {recordPayout.isError && (
            <div className="banner crit" style={{ marginBottom: 12 }}>
              <span>{apiErrorText(recordPayout.error, "Couldn't record the payout. Please retry.")}</span>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn ghost" onClick={closePay}>
              Cancel
            </button>
            <button
              className="btn primary"
              disabled={recordPayout.isPending}
              onClick={() =>
                recordPayout.mutate({ orgId: selected.orgId, periodStart: payPeriod.periodStart })
              }
            >
              {recordPayout.isPending ? "Recording…" : "Record payout"}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
